import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { addRecentAsset, addRecentInventoryAsset } from "../../globalStore/slices/recents/recentsSlice";
import { Box, Chip, Paper, Typography, useTheme } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EventIcon from "@mui/icons-material/Event";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import { getAssetProps, saveFilterSettings } from "./api";
import { formatLatLon, getFacilityNameByRadius } from "../../utils/lat_lon_utils";
import { getClassificationsByType } from "../../utils/getClassificationsByType";
import { isEqual } from "lodash";
import { thunks } from "../../globalStore/slices/assetHistory/assetHistorySlice";
import { setCurrentSnapshotAsset, updateUserPropertiesMap } from "../../globalStore/slices/user/userSlice";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTreeView } from "./hooks/useTreeView";
import * as Tab from "./Tabs/index";
import Grid from "@mui/material/Grid2";
import HorizontalTabs from "../../components/Tabs/HorizontalTabs";
import Loading from "../../components/Loading/Loading";
import Map from "../../components/Maps/AssetHistoryMap/AssetHistoryMap";
import MaterialConfirmationModal from "../../components/Modals/MaterialConfirmationModal";
import ModalDialog from "../../components/Modals/ModalDialog/ModalDialog";
import MaterialUiButton from "../../components/Buttons/MaterialUiButton/MaterialUiButton";

// Component uses the xlsx library which is very heavy , which is why we are using lazy/Suspense
const Table = lazy(() => import("./Table"));

export default function AssetSnapshot(props) {
  const {
    apiUrl,
    match,
    organizationId,
    timeZone,
    token,
    userId,
    usersConsoleRole,
  } = props;
  const theme = useTheme();
  const { palette = {} } = theme;
  const { info = {}, text = {}, primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";
  
  // Theme-aware colors
  const navyColor = primary?.main || "#002843";
  const greenColor = success?.main || "#A0C46D";
  const bgHeader = isDark ? "#021e2d" : "#f8f9fa";
  const borderColor = isDark ? "rgba(160, 196, 109, 0.2)" : "#e0e0e0";
  const textPrimary = isDark ? "#ffffff" : "#2c3e50";
  const textSecondary = text?.secondary || (isDark ? "#94a3b8" : "#64748b");
  const iconColor = isDark ? greenColor : "#1976d2";
  const classes = {
    root: {
      flexGrow: 1,
    },
    paper: {
      padding: theme.spacing(1.5, 2, 2),
      textAlign: "center",
      color: text?.secondary,
      height: "100%",
      width: "100%",
      minHeight: "66vh",
      maxHeight: "100%",
      backgroundColor: isDark ? "#032d42" : "#fff",
      borderRadius: "16px",
      border: isDark ? "1px solid rgba(160, 196, 109, 0.15)" : "1px solid #e0e0e0",
      boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.08)",
    },
  };
  const { assetId = "" } = match.params;
  const history = useHistory();
  const { searchHistories } = thunks.assetHistory;
  const dispatchGlobal = useDispatch();
  const cameFromRoot = props.history?.location?.state?.from === "/";

  // OK to destructure organization data because it's not going to change in the snapshot page
  const {
    facilities,
    devices,
    classifications,
    customs,
    organization,
    zones,
    usersMap,
  } = useSelector((state) => state.organization, isEqual);
  const assetSnapshotSettings = useSelector(
    (state) => state.user.userPropertiesMap.propertiesMap?.assetSnapshotSettings
  );

  let inventoryHistoryClassifications = getClassificationsByType({
    assetMode: "Inventory",
    isObject: true
  });

  const [loading, setLoading] = useState(false);
  const [infoModal, setInfoModal] = useState({
    content: "",
    modalShow: false,
    title: "",
  });

  const [activeMarker, setActiveMarker] = useState(null);

  const modalClose = () => {
    setModal({
      ...modal,
      modalShow: false,
    });
    setInfoModal({
      ...infoModal,
      modalShow: false,
    });
  };

  const [modal, setModal] = useState({
    modalShow: false,
    text: "",
    isError: false,
  });

  // Array.from() creates a new reference from the read-only redux state objects
  const assetTypes =
    organization && organization.assetTypes
      ? Array.from(organization.assetTypes).sort()
      : [];

  // If the user is NOT an admin, we remove the Tag Destroyed event
  const eventTypes = organization?.eventTypesMap
    ? Object.keys(organization.eventTypesMap)
      .sort()
      .filter((item) => {
        // Logic below says, "Only Admin users will see the 'Tag Destoyed' event"
        if (usersConsoleRole?.some((role) => role.name === "Admin")) {
          return item;
        } else {
          return item !== "Tag Destroyed";
        }
      })
    : [];

  const facilityArray = facilities
    ? Object.values(facilities).sort((a, b) => {
      if (a.name && b.name) {
        return a.name.localeCompare(b.name);
      }
      return -1;
    })
    : [];

  // The "limit", "start", and "sort" filters are controlled from the "rows", "page" index, and column sorting headers on the table itself, not the filter drop down
  const initFilters = {
    assetId: "",
    assetIds: null,
    binLocation: null,
    defaultColumnOrder: [],
    endDate: null,
    events: null,
    hideScans: true,
    limit: 25,
    locals: null,
    locations: null,
    proximity: false,
    startDate: null,
    tz: timeZone,
    users: null,
    sorted: [
      {
        id: "timeOfLog",
        desc: true,
      },
    ],
    zones: null,
    // spreading the saved filter settings under the default init filters will override any of the defaults, if they are present in the settings
    ...(assetSnapshotSettings || {}),
  };

  const deviceLogInitFilters = {
    devices: null,
    limit: 25,
  };

  const [state, setState] = useState({
    assetData: {},
    activeTab: 0,
    availableZones: Object.keys(zones).map((zone) => {
      // TODO: Is this how we are going to handle the differences between
      // pZones and tZones?
      return {
        internalZoneType: zones[zone].internalZoneType || null,
        label: zones[zone].name,
        value: zones[zone].zoneId,
      };
    }),
    deviceData: {},
    deviceLogFilters: {
      ...deviceLogInitFilters,
      start: 0,
      tz: timeZone,
    },
    devicePage: 0,
    filters: {
      ...initFilters,
      start: 0,
      tz: timeZone,
    },
    lists: {
      assetCategories: assetTypes,
      eventTypes: eventTypes,
      facilityArray: facilityArray,
      eventTypesMap: organization.eventTypesMap,
      usersMap: usersMap,
    },
    mounted: false,
    page: 0,
    showDeviceTab: false,
    trips: [],
  });
  const [mapSettings, setMapSettings] = useState(() => {
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem("atlas_map_type_id") : null;
    const mapTypeId = saved === "roadmap" || saved === "satellite" ? saved : "satellite";
    return { center: { lat: 34.561187, lng: -40.814914 }, mapTypeId, zoom: 5 };
  });
  useEffect(() => {
    if (mapSettings?.mapTypeId && (mapSettings.mapTypeId === "roadmap" || mapSettings.mapTypeId === "satellite")) {
      localStorage.setItem("atlas_map_type_id", mapSettings.mapTypeId);
    }
  }, [mapSettings?.mapTypeId]);

  // Commented out code. This was written for BFA but is no longer in use and the platform has since
  // evolved. This code is being kept here for reference in case we need to revisit this in the future.
  // const hasTelemetry = devices[state?.assetData.parentId] || false;

  // refs work to control our runaway use effects... if the history or device filters have changed, it will fire an effect that searches for data.
  // In the effects, we check to make sure that the previous filters are different than the incoming filters by using refs.
  // If they are different, we then reset the ref.current to the incoming filters. That way, we can control runaway use effects.
  const prevFiltersRef = useRef();

  const locationRef = useRef(props.history.location.pathname);
  const mapRef = useRef(null);
  const pathName = props.history.location.pathname;

  // Certain elements need to changed depending on which route the user comes in on.
  // In thise case 'inventoryStatus'. The Map disapears / Certain tabs do not render
  const isInventorySnapshot =
    history?.location?.pathname?.split("/")[1] === "inventorySnapshot";

  function onRowClick(row) {
    const { latitude, longitude } = row;
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      setMapSettings((prevState) => ({
        ...prevState,
        center: { lat: latitude, lng: longitude },
      }));
    }
    setActiveMarker(row?.assetHistoryId);
  }

  const handleSave = (filters) => {
    saveFilterSettings({ apiUrl, token, userId }, filters).then((res) => {
      if (res.error) {
        console.log(res.error);
      } else {
        const { appUser = {} } = res;
        const { propertiesMap = {} } = appUser;
        const { assetSnapshotSettings = {} } = propertiesMap;

        dispatchGlobal(
          updateUserPropertiesMap({
            assetSnapshotSettings,
          })
        );
      }
    });
  };

  // CustomControl is to help manage which tab is selected on the tabbed tables
  const customControl = (e, index) => {
    setState((prevState) => ({ ...prevState, activeTab: index }));
  };

  // custom hook for building hiearchy tree, see ./hooks/
  const treeView = useTreeView({
    facilities,
    history,
    assetId,
    isDark,
    organizationId,
    apiUrl,
    token,
    zones,
    assetData: state.assetData,
    mounted: state.mounted,
  });

  // first effect, get asset props and set mounted to true
  useEffect(() => {
    let isCancelled = false;

    getAssetProps({ apiUrl, token, organizationId }, assetId).then((res) => {
      if (isCancelled) return; // Prevent state update if unmounted

      if (res?.error) {
        setModal({
          modalShow: true,
          text: `Uh-oh! Something went wrong while fetching asset data... ${res.error}`,
          isError: true,
        });
      } else {
        const parentId = res?.asset ? res.asset.parentId : "";
        // TODO: Insert the device call here and return the device data with the setState
        // initialize state, changing the state.filters will fire useEffect to retrieve data

        const device =
          Object.values(devices).find(
            (element) => element.assetId === parentId
          ) || {};

        const showDeviceTab = device ? true : false;
        setState((prevState) => {
          return {
            ...prevState,
            assetData: {
              ...res?.asset,
            },
            mounted: true,
            deviceData: device.propertiesMap,
            showDeviceTab,
          };
        });
        dispatchGlobal(setCurrentSnapshotAsset(res?.asset ?? null));
      }
      //curr approach is every asset that is not an inventory asset is an asset, be it container, managed, device, etc
      if (res?.asset?.assetMode === "Inventory") {
        dispatchGlobal(addRecentInventoryAsset({ assetId: assetId, assetTag: res?.asset?.tag }));
      } else {
        dispatchGlobal(addRecentAsset({ assetId: assetId, assetTag: res?.asset?.tag }));
      }
    });

    return () => {
      isCancelled = true; // Cancel pending operations
      dispatchGlobal(setCurrentSnapshotAsset(null));
      // Resets the users Filters
      setState((prevState) => ({
        ...prevState,
        filters: {
          assetIds: null,
          endDate: null,
          events: null,
          locals: null,
          locations: null,
          pca: false,
          startDate: null,
          users: null,
        },
        mounted: false,
        assetData: {},
      }));
    };
  }, [apiUrl, organizationId, userId, token, assetId, devices]);

  useEffect(() => {
    if (
      !isEqual(state?.filters, prevFiltersRef.current) ||
      !isEqual(pathName, locationRef.current)
    ) {
      prevFiltersRef.current = state?.filters;
      setLoading(true);

      // Search Histories
      dispatchGlobal(
        searchHistories({
          assetId,
          filters: state?.filters,
          isExport: false,
          isInventorySnapshot: true,
          page: state?.page,
        })
      ).then((res) => {
        if (res.error) {
          setModal({
            modalShow: true,
            text: `Uh-oh! Something went wrong while fetching asset data... ${res.error}`,
            isError: true,
          });
          setLoading(false);
        } else {
          setState((prevState) => {
            locationRef.current = pathName;
            return {
              ...prevState,
              page: Math.floor(
                prevState.filters.start / prevState.filters.limit
              ),
              histories: res.payload,
            };
          });
          setLoading(false);
        }
      });

      // Search for Trips - Commenting out until we further mature polyline logic
      // retrieveAssetTrips({ apiUrl, token, organizationId, assetId }).then(res => {
      //   const { assetHistories = [], success = false } = res
      //   let tripsArray = []
      //   let tempTrip = []
      //   const polylineColors = ['red', 'blue', 'purple', 'black']

      //   // Here is where we do the logic of parsing the trips. We hit a .reverse() because the records coming in are the latest ones.

      //   assetHistories.reverse().forEach((history, index) => {
      //     const { event, latitude = '', longitude = '', timeOfLogLong = '' } = history
      //     const tripNumber = tripsArray.length + 1

      //     // If we already have an active trip going and we get a new "Depart", that means a new trip has started.
      //     // This is an assumption that I need to verify with the backend on.
      //     if (event === 'Depart' && tempTrip.length > 0) {

      //       tripsArray.push({
      //         ...history,
      //         color: polylineColors[tripNumber - 1],
      //         label: `Trip ${tripNumber}`,
      //         path: tempTrip,
      //       })

      //       tempTrip = []
      //       tempTrip.push({ lat: latitude, lng: longitude, timeOfLogLong })
      //     }
      //     else if (event === 'Depart') {
      //       tempTrip.push({ lat: latitude, lng: longitude, timeOfLogLong })
      //     } else if (event === 'Arrive') {
      //       tempTrip.push({ lat: latitude, lng: longitude, timeOfLogLong })

      //       tripsArray.push({
      //         ...history,
      //         color: polylineColors[tripNumber - 1],
      //         label: `Trip ${tripNumber}`,
      //         path: tempTrip
      //       })

      //       tempTrip = []
      //     } else if (event === 'EnRoute') {
      //       tempTrip.push({ lat: latitude, lng: longitude, timeOfLogLong })
      //     }
      //   })

      //   // Here we trim the tripsArray to only the 4 most recent trips.
      //   tripsArray = tripsArray.slice(0, 4)

      //   if (success) {
      //     setState(prevState => ({
      //       ...prevState,
      //       trips: tripsArray
      //     }))
      //   }
      // })
    }
  }, [
    pathName,
    assetId,
    apiUrl,
    token,
    organizationId,
    userId,
    state?.filters,
    state?.page,
    dispatchGlobal,
    searchHistories,
    isInventorySnapshot,
  ]);

  return (
    <Box sx={classes.root} mt={4}>
      {state && state.mounted ? (
        <Grid
          container
          spacing={3}
          sx={{
            margin: "0 auto",
            width: "84vw",
          }}
        >
          <ModalDialog
            handleClose={modalClose}
            open={infoModal.modalShow}
            title={infoModal.title}
            content={
              <Grid container>
                <Grid size={12}>{infoModal.content}</Grid>
              </Grid>
            }
          />

          {/* Need to add a "Go back to splash page" button here. Will probably be achieved with react-router */}

          {cameFromRoot && (
            <Grid size={12}>
              <MaterialUiButton label="Go Back" onClick={() => props.history.push("/")} />
            </Grid>
          )}

          {/* Map */}
          <Grid
            size={{
              sm: 12,
              md: 12,
              lg: 4,
            }}
          >
            <Paper
              sx={{
                ...classes.paper,
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${borderColor}`,
              }}
            >
              {/* Map Header — journey controls in app area, not on map */}
              {(() => {
                const raw = state?.assetHistories ?? state?.histories;
                const histories = Array.isArray(raw) ? raw : (Array.isArray(raw?.histories) ? raw.histories : []);
                const hasJourney = histories.length >= 2;
                const hasMarkers = histories.length >= 1;
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 0.75,
                      padding: "8px 14px",
                      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.12)" : "rgba(0,40,67,0.06)"}`,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.625 }}>
                      <MapIcon sx={{ color: iconColor, fontSize: "1.0625rem" }} />
                      <Typography sx={{ fontWeight: 600, color: textPrimary, fontSize: "0.8125rem", letterSpacing: "0.02em" }}>
                        Asset Location
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                      <MaterialUiButton
                        color="primary"
                        disabled={!hasJourney}
                        label="Fit path"
                        onClick={() => mapRef.current?.fitJourneyBounds?.()}
                        size="small"
                        variant="contained"
                        sx={{ minHeight: 28, padding: "0 10px", fontSize: "0.75rem" }}
                      />
                      <MaterialUiButton
                        color="primary"
                        disabled={!hasMarkers}
                        label="Go to current"
                        onClick={() => mapRef.current?.centerOnLatest?.()}
                        size="small"
                        variant="contained"
                        sx={{ minHeight: 28, padding: "0 10px", fontSize: "0.75rem" }}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.375,
                        padding: "3px 8px",
                        borderRadius: "8px",
                        border: `1px solid ${isDark ? "rgba(160,196,109,0.35)" : "rgba(46,125,50,0.35)"}`,
                      }}
                    >
                      <Box className="map-live-pulse-dot" sx={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: greenColor }} />
                      <Typography sx={{ color: isDark ? "#81c784" : "#2e7d32", fontWeight: 600, fontSize: "0.6875rem", letterSpacing: "0.02em" }}>
                        Live
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
              
              {/* Map Container */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: "calc(66vh - 50px)",
                  position: "relative",
                  "& > div": {
                    borderRadius: "0 0 16px 16px",
                  },
                }}
              >
                <Map
                  ref={mapRef}
                  activeMarker={activeMarker}
                  eventTypesMap={
                    state.lists.eventTypesMap ? state.lists.eventTypesMap : {}
                  }
                  facilities={facilities}
                  mapSettings={mapSettings}
                  setActiveMarker={setActiveMarker}
                  setMapSettings={setMapSettings}
                  state={state}
                  timeZone={timeZone}
                />
              </Box>
              {/* Legend below map — premium, same shapes as on map */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 0,
                  padding: "10px 16px",
                  borderTop: `1px solid ${borderColor}`,
                  background: isDark
                    ? "linear-gradient(180deg, rgba(160,196,109,0.08) 0%, rgba(160,196,109,0.03) 100%)"
                    : "linear-gradient(180deg, rgba(160,196,109,0.06) 0%, rgba(160,196,109,0.02) 100%)",
                  boxShadow: isDark ? "inset 0 1px 0 rgba(255,255,255,0.03)" : "inset 0 1px 0 rgba(255,255,255,0.5)",
                }}
              >
                {/* Path */}
                <Box title="Route line between stops" sx={{ display: "flex", alignItems: "center", gap: 0.625, px: 1.25, borderRight: `1px solid ${isDark ? "rgba(148,163,184,0.15)" : "rgba(0,40,67,0.08)"}` }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 3,
                      borderRadius: "2px",
                      background: `linear-gradient(90deg, ${greenColor} 0%, rgba(160,196,109,0.85) 100%)`,
                      boxShadow: `0 1px 2px rgba(0,0,0,0.06)`,
                    }}
                  />
                  <Typography component="span" sx={{ color: textSecondary, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.03em" }}>
                    Path
                  </Typography>
                </Box>
                {/* Current */}
                <Box title="Latest reported location" sx={{ display: "flex", alignItems: "center", gap: 0.625, px: 1.25, borderRight: `1px solid ${isDark ? "rgba(148,163,184,0.15)" : "rgba(0,40,67,0.08)"}` }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      background: greenColor,
                      border: "2px solid #fff",
                      boxShadow: `0 0 0 1px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.08)`,
                    }}
                  />
                  <Typography component="span" sx={{ color: textSecondary, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.03em" }}>
                    Current
                  </Typography>
                </Box>
                {/* Labels */}
                <Box title="Time at this stop" sx={{ display: "flex", alignItems: "center", gap: 0.625, px: 1.25 }}>
                  <Box
                    sx={{
                      padding: "3px 7px",
                      borderRadius: "6px",
                      background: isDark
                        ? "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)"
                        : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                      border: `1px solid ${isDark ? "rgba(148,163,184,0.2)" : "rgba(0,40,67,0.1)"}`,
                      boxShadow: isDark ? "0 1px 4px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.04)" : "0 1px 4px rgba(0,0,0,0.06), 0 0 0 1px rgba(255,255,255,0.8)",
                      fontSize: "0.5625rem",
                      fontWeight: 700,
                      color: textSecondary,
                      letterSpacing: "0.02em",
                    }}
                  >
                    time
                  </Box>
                  <Typography component="span" sx={{ color: textSecondary, fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.03em" }}>
                    Labels = time at stop
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Details */}
          <Grid
            size={{
              sm: 12,
              md: 12,
              lg: 8,
            }}
          >
            <Paper sx={classes.paper}>
              <HorizontalTabs
                allowScrollButtonsMobile
                ariaLabel={isInventorySnapshot ? "Inventory Snapshot: Overview, Edit, Update Event, Attachments" : "Asset Snapshot: Overview, Edit Asset, Update Event, Attachments"}
                customControl={customControl}
                customValue={state.activeTab}
                fullWidth={true}
                premium
                useBox
                variant="scrollable"
              >
                <Tab.Overview
                  assetData={state.assetData}
                  classifications={classifications}
                  eventTypesMap={organization.eventTypesMap}
                  facilities={facilities}
                  isInventorySnapshot={isInventorySnapshot}
                  label={
                    isInventorySnapshot
                      ? "Inventory Snapshot"
                      : "Asset Snapshot"
                  }
                  setState={setState}
                  state={state}
                  treeView={treeView}
                />

                {isInventorySnapshot ? null : (
                  <Tab.EditAsset
                    apiUrl={apiUrl}
                    assetData={state.assetData}
                    classifications={classifications}
                    facilities={facilities}
                    label={"Edit Asset"}
                    organization={organization}
                    setModal={setModal}
                    setState={setState}
                    state={state}
                    token={token}
                    userId={userId}
                  />
                )}

                {
                  <Tab.Update
                    apiUrl={apiUrl}
                    assetData={state.assetData}
                    eventTypes={state.lists.eventTypes}
                    eventTypesMap={organization.eventTypesMap}
                    facilities={facilities}
                    label="Update Event"
                    setModal={setModal}
                    setState={setState}
                    state={state}
                    token={token}
                    userId={userId}
                  />
                }

                {/* assemblies disabled for now. May be brought back */}
                {/* <Tab.Assemblies
                  apiUrl={apiUrl}
                  assetData={state.assetData}
                  label="Assemblies"
                  setState={setState}
                  state={state}
                  token={token}
                /> */}

                <Tab.Attachments
                  apiUrl={apiUrl}
                  assetData={state.assetData}
                  label="Attachments"
                  setState={setState}
                  state={state}
                  token={token}
                />
              </HorizontalTabs>
            </Paper>
          </Grid>

          {/* Table */}
          <Grid size={12}>
            {loading ? <Loading color={info?.main} /> : null}{" "}
            <Suspense fallback={<Loading color={info?.main} />}>
              <Table
                activeTab={state.activeTab}
                apiUrl={apiUrl}
                assetData={state.assetData}
                assetId={assetId}
                // TODO: since this is all purpose component, need to set classifications based on assetMode
                classifications={inventoryHistoryClassifications}
                customs={customs}
                customControl={customControl}
                defaultColumnOrder={state.filters.defaultColumnOrder}
                devices={devices}
                dispatchGlobal={dispatchGlobal}
                eventTypes={state.lists.eventTypes}
                eventTypesMap={organization.eventTypesMap}
                facilities={facilities}
                facilityArray={state.lists.facilityArray}
                handleSave={handleSave}
                isInventorySnapshot={isInventorySnapshot}
                onRowClick={onRowClick}
                organizationId={organizationId}
                searchHistories={searchHistories}
                setInfoModal={setInfoModal}
                setLoading={setLoading}
                setMapSettings={setMapSettings}
                setModal={setModal}
                setState={setState}
                state={state}
                timeZone={state.filters.tz ? state.filters.tz : ""}
                token={token}
                treeView={treeView}
                userId={userId}
                usersMap={usersMap}
                useSelector={useSelector}
                zones={zones}
              />
            </Suspense>
          </Grid>
        </Grid>
      ) : (
        <Loading color={info?.main} opaque={true} />
      )}
      <MaterialConfirmationModal
        content={modal.text}
        closeModal={() => {
          setModal({ ...modal, modalShow: false });
        }}
        modalOpen={modal.modalShow}
        severity={modal.isError ? "error" : "success"}
        variant="filled"
      />
    </Box>
  );
}